import { useMyContext } from 'Context/MyContextProvider';
import {
    Button,
    Col,
    Divider,
    Form,
    Input,
    Row,
    Select,
    Switch,
    Tooltip,
    Typography,
    message,
} from 'antd';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import apiClient from 'services/apiClient';
import Flex from 'components/shared-components/Flex';
import { ROW_GUTTER } from 'constants/ThemeConstant';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

const { Text } = Typography;

const EMPTY_INTERNATIONAL_ROW = {
    country_iso2: undefined,
    marketing_margin: 0,
    utility_margin: 0,
    authentication_margin: 0,
};

const DEFAULT_INDIA_PRICING = {
    marketing_margin: 0,
    utility_margin: 0,
    authentication_margin: 0,
    service_margin: 0,
};

const MARGIN_DECIMALS = 4;
const SELL_DECIMALS = 2;

const roundToDecimals = (value, decimals = MARGIN_DECIMALS) => {
    const factor = 10 ** decimals;
    return Math.round(Number(value || 0) * factor) / factor;
};

const formatOverhead = (value) => `₹${roundToDecimals(value, MARGIN_DECIMALS).toFixed(MARGIN_DECIMALS)}`;

const formatSell = (value) => `₹${roundToDecimals(value, SELL_DECIMALS).toFixed(SELL_DECIMALS)}`;

const normalizeMargin = (value) => roundToDecimals(value, MARGIN_DECIMALS);

const marginFromLegacySell = (legacySellPrice, metaPrice) => {
    const sell = roundToDecimals(legacySellPrice, SELL_DECIMALS);
    return Math.max(0, roundToDecimals(sell - Number(metaPrice || 0), MARGIN_DECIMALS));
};

const formatMetaInr = (value) => `₹${Number(value || 0).toFixed(4)}`;

const computeFinalPrice = ({ metaPrice, margin, isService = false, missingMeta = false }) => {
    const overhead = normalizeMargin(margin);
    const meta = Number(metaPrice || 0);

    if (missingMeta) {
        return {
            final: overhead,
            tooltip: `Overhead ${formatOverhead(overhead)} — select country to apply Meta base`,
        };
    }

    if (isService) {
        return {
            final: overhead,
            tooltip: `Meta free + ${formatOverhead(overhead)} overhead`,
        };
    }

    const sell = roundToDecimals(meta + overhead, SELL_DECIMALS);

    return {
        final: sell,
        tooltip: `Meta ${formatMetaInr(meta)} + ${formatOverhead(overhead)} = ${formatSell(sell)}`,
    };
};

const FinalPriceSuffix = (props) => {
    const { final, tooltip } = computeFinalPrice(props);

    return (
        <Tooltip title={tooltip}>
            <Text
                strong
                style={{
                    color: '#389e0d',
                    fontSize: 13,
                    whiteSpace: 'nowrap',
                    cursor: 'help',
                }}
            >
                {formatSell(final)}
            </Text>
        </Tooltip>
    );
};

const overheadNormalize = (value) => {
    if (value === '' || value === null || value === undefined) {
        return 0;
    }
    return normalizeMargin(value);
};

const OverheadInput = React.forwardRef(({
    suffixProps,
    disabled,
    value,
    onChange,
    onBlur,
    ...props
}, ref) => {
    const [focused, setFocused] = useState(false);
    const [draft, setDraft] = useState('');

    const formattedValue = value === undefined || value === null || value === ''
        ? ''
        : normalizeMargin(value).toFixed(MARGIN_DECIMALS);

    const displayValue = focused ? draft : formattedValue;

    return (
        <Input
            ref={ref}
            disabled={disabled}
            value={displayValue}
            placeholder="0.0000"
            suffix={<FinalPriceSuffix {...suffixProps} />}
            onFocus={() => {
                setFocused(true);
                setDraft(formattedValue);
            }}
            onChange={(event) => {
                const next = event.target.value;
                setDraft(next);
                if (next === '' || next === '.') {
                    onChange?.(0);
                    return;
                }
                const parsed = Number(next);
                if (!Number.isNaN(parsed)) {
                    onChange?.(parsed);
                }
            }}
            onBlur={(event) => {
                setFocused(false);
                const parsed = Number(event.target.value);
                if (!Number.isNaN(parsed)) {
                    onChange?.(normalizeMargin(parsed));
                }
                onBlur?.(event);
            }}
            onKeyDown={(event) => {
                const { key, target } = event;
                if (key === 'Backspace' || key === 'ArrowLeft' || key === 'ArrowRight' || key === 'Tab') {
                    return;
                }
                if (!(/^\d$/.test(key) || key === '.')) {
                    event.preventDefault();
                    return;
                }
                if (key === '.' && target.value.includes('.')) {
                    event.preventDefault();
                    return;
                }
                if (target.value.includes('.')) {
                    const decimalPart = target.value.substring(target.value.indexOf('.') + 1);
                    if (decimalPart.length >= MARGIN_DECIMALS) {
                        event.preventDefault();
                    }
                }
            }}
            {...props}
        />
    );
});

OverheadInput.displayName = 'OverheadInput';

const INDIA_OVERHEAD_FIELDS = [
    { name: ['india_pricing', 'marketing_margin'], label: 'Marketing overhead', metaKey: 'marketing_price' },
    { name: ['india_pricing', 'utility_margin'], label: 'Utility overhead', metaKey: 'utility_price' },
    { name: ['india_pricing', 'service_margin'], label: 'Service overhead', metaKey: null, isService: true },
    { name: ['india_pricing', 'authentication_margin'], label: 'Auth overhead', metaKey: 'authentication_price' },
];

const INTERNATIONAL_OVERHEAD_FIELDS = [
    { name: 'marketing_margin', label: 'Marketing overhead', metaKey: 'marketing_price' },
    { name: 'utility_margin', label: 'Utility overhead', metaKey: 'utility_price' },
    { name: 'authentication_margin', label: 'Auth overhead', metaKey: 'authentication_price' },
];

const BillingPricingModel = ({ api, authToken, id }) => {
    const { Permisson } = useMyContext();
    const themeData = useSelector((auth) => auth.theme);
    const [priceAlert, setPriceAlert] = useState();
    const [internationalGatewayEnabled, setInternationalGatewayEnabled] = useState(false);
    const [countryOptions, setCountryOptions] = useState([]);
    const [metaRatesByCountry, setMetaRatesByCountry] = useState({});
    const [indiaMeta, setIndiaMeta] = useState(null);
    const [disable, setDisable] = useState(true);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const [form] = Form.useForm();
    const indiaPricingWatch = Form.useWatch('india_pricing', form) || DEFAULT_INDIA_PRICING;
    const internationalPricingWatch = Form.useWatch('international_pricing', form) || [];
    const canEdit = Permisson?.includes('Add or Update Pricing Model');

    const fetchMetaCountries = useCallback(async () => {
        try {
            const response = await apiClient.get(`${api}meta-pricing`, {
                headers: { Authorization: `Bearer ${authToken}` },
            });
            const rows = response?.data?.data || [];
            const ratesByCountry = rows.reduce((acc, row) => {
                if (row?.country_iso2) {
                    acc[row.country_iso2] = row;
                }
                return acc;
            }, {});
            setMetaRatesByCountry(ratesByCountry);

            const india = ratesByCountry.IN || null;
            setIndiaMeta(india);

            const options = rows
                .filter((row) => row.country_iso2 && row.country_iso2 !== 'IN')
                .map((row) => ({
                    value: row.country_iso2,
                    label: `${row.market_name || row.country_iso2} (${row.country_iso2})`,
                }));
            setCountryOptions(options);

            return india;
        } catch (error) {
            console.error(error);
            return null;
        }
    }, [api, authToken]);

    const fetchPricingData = useCallback(async (metaIndia) => {
        setLoading(true);
        try {
            const [historyResponse, userPricingResponse] = await Promise.all([
                apiClient.get(`${api}pricing-history/${id}`, {
                    headers: { Authorization: `Bearer ${authToken}` },
                }),
                apiClient.get(`${api}user-pricing/${id}`, {
                    headers: { Authorization: `Bearer ${authToken}` },
                }),
            ]);

            const history = historyResponse?.data?.pricing_history || [];
            const legacyPricing = history.length > 0 ? history[0] : null;

            const data = userPricingResponse?.data?.data || {};
            const enabled = data.international_gateway_enabled === true;
            const internationalPricing = Array.isArray(data.international_pricing)
                ? data.international_pricing
                : [];

            if (data.price_alert !== undefined && data.price_alert !== null) {
                setPriceAlert(parseFloat(data.price_alert));
            } else if (legacyPricing) {
                setPriceAlert(parseFloat(legacyPricing.price_alert));
            }

            let indiaPricing = data.india_pricing;
            if (!indiaPricing || (
                Number(indiaPricing.marketing_margin) === 0
                && Number(indiaPricing.utility_margin) === 0
                && Number(indiaPricing.authentication_margin) === 0
                && Number(indiaPricing.service_margin) === 0
                && legacyPricing
                && metaIndia
            )) {
                indiaPricing = {
                    marketing_margin: marginFromLegacySell(legacyPricing.marketing_price, metaIndia.marketing_price),
                    utility_margin: marginFromLegacySell(legacyPricing.utility_price, metaIndia.utility_price),
                    authentication_margin: marginFromLegacySell(legacyPricing.authentication_price, metaIndia.authentication_price),
                    service_margin: normalizeMargin(Math.max(0, Number(legacyPricing.service_price || 0))),
                };
            } else if (indiaPricing) {
                indiaPricing = {
                    marketing_margin: normalizeMargin(indiaPricing.marketing_margin),
                    utility_margin: normalizeMargin(indiaPricing.utility_margin),
                    authentication_margin: normalizeMargin(indiaPricing.authentication_margin),
                    service_margin: normalizeMargin(indiaPricing.service_margin),
                };
            }

            setInternationalGatewayEnabled(enabled);
            form.setFieldsValue({
                international_gateway_enabled: enabled,
                india_pricing: indiaPricing || DEFAULT_INDIA_PRICING,
                international_pricing: internationalPricing.length > 0
                    ? internationalPricing.map((row) => ({
                        ...row,
                        marketing_margin: normalizeMargin(row.marketing_margin),
                        utility_margin: normalizeMargin(row.utility_margin),
                        authentication_margin: normalizeMargin(row.authentication_margin),
                    }))
                    : [EMPTY_INTERNATIONAL_ROW],
            });
        } catch (error) {
            form.setFieldsValue({
                international_gateway_enabled: false,
                india_pricing: DEFAULT_INDIA_PRICING,
                international_pricing: [EMPTY_INTERNATIONAL_ROW],
            });
        } finally {
            setLoading(false);
        }
    }, [api, authToken, form, id]);

    const fetchMetaCountriesRef = useRef(fetchMetaCountries);
    const fetchPricingDataRef = useRef(fetchPricingData);
    fetchMetaCountriesRef.current = fetchMetaCountries;
    fetchPricingDataRef.current = fetchPricingData;

    useEffect(() => {
        if (canEdit) {
            setDisable(false);
        }
    }, [canEdit]);

    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            const metaIndia = await fetchMetaCountriesRef.current();
            if (cancelled) {
                return;
            }
            await fetchPricingDataRef.current(metaIndia);
        };

        load();

        return () => {
            cancelled = true;
        };
    }, [id, api, authToken]);

    const handleSubmit = async () => {
        setSaving(true);
        try {
            const values = await form.validateFields();
            const indiaPricing = {
                marketing_margin: normalizeMargin(values.india_pricing?.marketing_margin),
                utility_margin: normalizeMargin(values.india_pricing?.utility_margin),
                authentication_margin: normalizeMargin(values.india_pricing?.authentication_margin),
                service_margin: normalizeMargin(values.india_pricing?.service_margin),
            };

            const internationalPricing = (values.international_pricing || [])
                .filter((row) => row?.country_iso2)
                .map((row) => ({
                    country_iso2: row.country_iso2,
                    marketing_margin: normalizeMargin(row.marketing_margin),
                    utility_margin: normalizeMargin(row.utility_margin),
                    authentication_margin: normalizeMargin(row.authentication_margin),
                }));

            if (values.international_gateway_enabled && internationalPricing.length === 0) {
                message.error('Add at least one country for international gateway.');
                setSaving(false);
                return;
            }

            await apiClient.put(`${api}user-pricing/${id}`, {
                international_gateway_enabled: values.international_gateway_enabled === true,
                price_alert: priceAlert,
                india_pricing: indiaPricing,
                international_pricing: values.international_gateway_enabled ? internationalPricing : [],
            }, {
                headers: { Authorization: `Bearer ${authToken}` },
            });

            message.success('Pricing updated successfully');
            fetchPricingData(indiaMeta);
        } catch (error) {
            if (error?.errorFields) {
                setSaving(false);
                return;
            }
            const validationMessage = error?.response?.data?.errors
                ? Object.values(error.response.data.errors).flat().join(', ')
                : null;
            message.error(validationMessage || error?.response?.data?.message || 'Failed to update pricing');
        } finally {
            setSaving(false);
        }
    };

    const handleKeyDown = (event) => {
        const { key, target } = event;
        if (key === 'Backspace' || key === 'ArrowLeft' || key === 'ArrowRight') {
            return;
        }
        if (!(/^\d$/.test(event.key) || event.key === '.')) {
            event.preventDefault();
        } else if (target.value.includes('.')) {
            const decimalIndex = target.value.indexOf('.');
            const decimalPart = target.value.substring(decimalIndex + 1);
            if (decimalPart.length >= MARGIN_DECIMALS) {
                event.preventDefault();
            }
        }
    };

    if (loading) {
        return null;
    }

    return (
        <Col xs={24} sm={24} md={24} lg={24} xl={16}>
            <div
                className="border-0 rounded p-3"
                style={{
                    background: themeData.currentTheme === 'dark' ? '#1b2531' : '#fafafb',
                    boxShadow: 'rgba(27, 37, 49, 0.1) 0px 2px 10px',
                }}
            >
                <h4 className="mb-0">Pricing Model</h4>
                <div className="balance-form">
                    <Form form={form} layout="vertical" disabled={disable}>
                        <Form.Item label="Low credit alert">
                            <Input
                                size="middle"
                                value={priceAlert}
                                onKeyDown={handleKeyDown}
                                onChange={(e) => setPriceAlert(parseFloat(e.target.value) || 0)}
                            />
                        </Form.Item>

                        <Divider style={{ margin: '8px 0 16px' }} />

                        <Text strong>India (Domestic)</Text>
                        <Text type="secondary" style={{ display: 'block', marginBottom: 12, fontSize: 13 }}>
                            Overhead in field · green suffix is final sell price (hover for Meta breakdown)
                        </Text>

                        <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
                            {INDIA_OVERHEAD_FIELDS.map((field) => {
                                const marginKey = field.name[1];
                                const marginValue = indiaPricingWatch?.[marginKey];

                                return (
                                    <Col xs={24} sm={12} key={field.label}>
                                        <Form.Item
                                            name={field.name}
                                            label={field.label}
                                            rules={[{ required: true, message: 'Required' }]}
                                            normalize={overheadNormalize}
                                        >
                                            <OverheadInput
                                                suffixProps={{
                                                    metaPrice: field.isService ? 0 : indiaMeta?.[field.metaKey],
                                                    margin: marginValue,
                                                    isService: field.isService,
                                                }}
                                            />
                                        </Form.Item>
                                    </Col>
                                );
                            })}
                        </Row>

                        <Divider style={{ margin: '12px 0 16px' }} />

                        <Form.Item
                            name="international_gateway_enabled"
                            label="Enable international gateway"
                            valuePropName="checked"
                        >
                            <Switch
                                onChange={(checked) => {
                                    setInternationalGatewayEnabled(checked);
                                    if (checked) {
                                        const current = form.getFieldValue('international_pricing');
                                        if (!Array.isArray(current) || current.length === 0) {
                                            form.setFieldsValue({
                                                international_pricing: [EMPTY_INTERNATIONAL_ROW],
                                            });
                                        }
                                    }
                                }}
                            />
                        </Form.Item>

                        {internationalGatewayEnabled && (
                            <>
                                <Text type="secondary" style={{ display: 'block', marginBottom: 12, fontSize: 13 }}>
                                    Additional countries (overhead on top of Meta rates)
                                </Text>
                                <Form.List name="international_pricing">
                                    {(fields, { add, remove }) => (
                                        <>
                                            {fields.map(({ key, name, ...restField }) => {
                                                const rowValues = internationalPricingWatch?.[name] || {};
                                                const rowMeta = metaRatesByCountry[rowValues.country_iso2] || null;

                                                const selectedOtherIso2s = (internationalPricingWatch || [])
                                                    .filter((_, idx) => idx !== name)
                                                    .map((row) => row?.country_iso2)
                                                    .filter(Boolean);

                                                const filteredOptions = countryOptions.filter(
                                                    (opt) => !selectedOtherIso2s.includes(opt.value)
                                                );

                                                return (
                                                <Row gutter={ROW_GUTTER} key={key} align="top" className="mb-2">
                                                    <Col xs={24} md={8}>
                                                        <Form.Item
                                                            {...restField}
                                                            name={[name, 'country_iso2']}
                                                            label={name === 0 ? 'Country' : ''}
                                                            rules={[{ required: true, message: 'Select country' }]}
                                                        >
                                                            <Select
                                                                showSearch
                                                                placeholder="Select country"
                                                                options={filteredOptions}
                                                                optionFilterProp="label"
                                                            />
                                                        </Form.Item>
                                                    </Col>
                                                    {INTERNATIONAL_OVERHEAD_FIELDS.map((field) => (
                                                        <Col xs={24} md={5} key={`${key}-${field.name}`}>
                                                            <Form.Item
                                                                {...restField}
                                                                name={[name, field.name]}
                                                                label={name === 0 ? field.label : ''}
                                                                rules={[{ required: true, message: 'Required' }]}
                                                                normalize={overheadNormalize}
                                                            >
                                                                <OverheadInput
                                                                    suffixProps={{
                                                                        metaPrice: rowMeta?.[field.metaKey],
                                                                        margin: rowValues?.[field.name],
                                                                        missingMeta: !rowValues?.country_iso2,
                                                                    }}
                                                                />
                                                            </Form.Item>
                                                        </Col>
                                                    ))}
                                                    <Col xs={24} md={1}>
                                                        {fields.length > 1 && (
                                                            <Button
                                                                type="text"
                                                                danger
                                                                icon={<MinusCircleOutlined />}
                                                                onClick={() => remove(name)}
                                                                style={{ marginTop: name === 0 ? 30 : 0 }}
                                                            />
                                                        )}
                                                    </Col>
                                                </Row>
                                                );
                                            })}
                                            <Button
                                                type="dashed"
                                                onClick={() => add(EMPTY_INTERNATIONAL_ROW)}
                                                icon={<PlusOutlined />}
                                                block
                                            >
                                                Add country
                                            </Button>
                                        </>
                                    )}
                                </Form.List>
                            </>
                        )}

                        {canEdit && (
                            <Flex justifyContent="end" className="mt-3">
                                <Button type="primary" loading={saving} onClick={handleSubmit}>
                                    Submit
                                </Button>
                            </Flex>
                        )}
                    </Form>
                </div>
            </div>
        </Col>
    );
};

export default BillingPricingModel;
